import React, { Key, useMemo, useState } from "react";

import { InternalOnlyChip } from "manager/components/vero1/InternalOnlyChip";
import { displayName } from "manager/components/vero1/Vero1ReviewPanel/formatItemValue";
import { useRecalculateLumen } from "manager/hooks/api/dealQueries";
import { useGetLumenV2RunById } from "manager/hooks/api/lumenQueries";
import { useDealLumenRuns } from "manager/hooks/api/vero1Queries";
import { LumenV2 } from "shared/components/vega/LumenV2";
import { usePermissions } from "shared/hooks";
import type { DealLumenRunSummary } from "shared/interfaces/api/vero1";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";

import { formatRunStamp } from "../vero1Dates";

import { CompactLumenRun } from "./CompactLumenRun";
import { RunCompare } from "./RunCompare";
import {
  FaintNote,
  GhostButton,
  HeaderActions,
  HeaderMeta,
  HeaderPad,
  HeaderRow,
  HeaderSelect,
  MetaLine,
  RunProgressChip,
  RunProgressSpinner,
  SectionBody,
  SectionCard,
  SectionTitle,
  TitleBlock,
  TitleLine,
  VerdictRule,
  SkeletonBar,
} from "./styled";

/* Spec 4.1 lower card — Lumen screening: run selector over every run
   (derived runs labeled), provenance subline, Compare and Re-run beside the
   thing they act on, and the verdict-toned 2px rule. 4.3: while a triggered
   re-run executes, the selector is replaced by the in-progress run line, the
   rows grey back rather than empty out, and both actions are unavailable
   until the run lands. */

const runOptionLabel = (
  run: DealLumenRunSummary,
  index: number,
  total: number
) =>
  `Run ${total - index} · ${formatRunStamp(run.timestamp)}${
    run.vero1Derived ? " · VERO1 override" : ""
  }`;

const VERDICT_WORDS: Record<string, string> = {
  APPROVE: "Approve",
  CONDITIONAL: "Conditional",
  DECLINE: "Decline",
};

const ruleTone = (scoreLabel?: string | null): string | undefined => {
  const label = (scoreLabel ?? "").toUpperCase();
  if (label.includes("APPROVE") || label.includes("EXCELLENT"))
    return "APPROVE";
  if (label.includes("DECLINE") || label.includes("NEGATIVE")) return "DECLINE";
  if (label.includes("CONDITION") || label.includes("REVIEW"))
    return "CONDITIONAL";
  return undefined;
};

/* 4.1 subline: "Decline · ran after the VERO1 review of Jul 10" */
const provenance = (
  run: DealLumenRunSummary,
  latestSubmittedAt?: string | null
): string => {
  const parts: string[] = [];
  const tone = ruleTone(run.scoreLabel);
  if (tone) parts.push(VERDICT_WORDS[tone]);
  else if (run.scoreLabel) parts.push(displayName(run.scoreLabel));
  if (run.vero1Derived && run.checklistVersion != null) {
    parts.push(`review v${run.checklistVersion}`);
  } else if (
    latestSubmittedAt &&
    run.timestamp > new Date(latestSubmittedAt).getTime()
  ) {
    parts.push(
      `ran after the VERO1 review of ${formatRunStamp(latestSubmittedAt)}`
    );
  }
  if (run.executedByName) parts.push(`triggered by ${run.executedByName}`);
  return parts.join(" · ");
};

/* the 2px rule reads the run's verdict; label text is client-configurable so
   match on the canonical words */

export const LumenScreeningSection: React.FC<{
  dealId: Key;
  latestLumenRun?: any;
  vero1Active?: boolean;
  latestSubmittedAt?: string | null;
}> = ({ dealId, latestLumenRun, vero1Active, latestSubmittedAt }) => {
  const { canRecalculateLumen } = usePermissions();
  // VERO1 audiences (and the redesign flag) get the run in the tab's own
  // row grammar; everyone else keeps the shared full-size LumenV2 view
  const compactRows =
    useVeroFlag(VeroFlag.useDealSummaryRedesignProduct1270) ||
    Boolean(vero1Active);
  const { runs, runsLoading, runsUnavailable } = useDealLumenRuns(dealId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [comparing, setComparing] = useState(false);
  const selected = runs.find((run) => run.id === selectedId) ?? runs[0] ?? null;

  // the deal page has already fetched the newest run in full — reuse it and
  // fetch by id only for historical selections
  const latestId = latestLumenRun?.id;
  const needsFetch = Boolean(selected?.id) && selected?.id !== latestId;
  const { lumenRun: fetchedRun } = useGetLumenV2RunById(
    selected?.id,
    needsFetch
  );
  const runDetail = needsFetch ? fetchedRun : latestLumenRun;

  const { recalculateLumen, isLoading: rerunning } = useRecalculateLumen({
    dealId,
    onSuccess: () => setSelectedId(null),
  });
  const startedAt = useMemo(
    () =>
      rerunning
        ? new Date().toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
          })
        : null,
    [rerunning]
  );

  const total = runs.length;
  const selectedIndex = selected
    ? runs.findIndex((run) => run.id === selected.id)
    : -1;
  const compareA = runs[0] ?? null;
  const compareB = runs[1] ?? null;

  const settling = runsLoading || (needsFetch && !fetchedRun);

  return (
    <SectionCard
      data-testid="deal-review-lumen-section"
      data-settling={settling || undefined}
    >
      <HeaderPad>
        <HeaderRow>
          <TitleBlock>
            <TitleLine>
              <SectionTitle>Lumen screening</SectionTitle>
              {rerunning ? (
                <RunProgressChip data-testid="run-in-progress-line">
                  <RunProgressSpinner aria-hidden />
                  Run {total + 1} in progress
                  {startedAt ? ` · started ${startedAt}` : ""}
                </RunProgressChip>
              ) : (
                total > 0 && (
                  <>
                    <HeaderSelect
                      aria-label="Lumen run"
                      value={selected?.id ?? ""}
                      onChange={(event) => {
                        setComparing(false);
                        setSelectedId(event.target.value);
                      }}
                    >
                      {runs.map((run, index) => (
                        <option key={run.id} value={run.id}>
                          {runOptionLabel(run, index, total)}
                        </option>
                      ))}
                    </HeaderSelect>
                    {selectedIndex === 0 && (
                      <FaintNote>latest of {total}</FaintNote>
                    )}
                  </>
                )
              )}
              <InternalOnlyChip $tone="dark">Internal only</InternalOnlyChip>
            </TitleLine>
            <MetaLine>
              {rerunning
                ? `Showing run ${Math.max(
                    total - Math.max(selectedIndex, 0),
                    1
                  )} until it finishes. Usually under a minute.`
                : selected
                ? provenance(selected, latestSubmittedAt)
                : ""}
            </MetaLine>
          </TitleBlock>
          <HeaderActions>
            <GhostButton
              type="button"
              disabled={rerunning || runs.length < 2}
              onClick={() => setComparing((prev) => !prev)}
            >
              {comparing ? "Back to run" : "Compare"}
            </GhostButton>
            {canRecalculateLumen && (
              <GhostButton
                type="button"
                disabled={rerunning}
                onClick={() => recalculateLumen()}
              >
                Re-run
              </GhostButton>
            )}
          </HeaderActions>
        </HeaderRow>
        <VerdictRule
          $tone={rerunning ? undefined : ruleTone(selected?.scoreLabel)}
        />
      </HeaderPad>
      <SectionBody $muted={rerunning} data-testid="lumen-run-body">
        {runsUnavailable && (
          <HeaderMeta>Run history is unavailable right now.</HeaderMeta>
        )}
        {!runsUnavailable && !runsLoading && total === 0 && (
          <HeaderMeta>No Lumen runs yet.</HeaderMeta>
        )}
        {settling &&
          !comparing &&
          !runDetail &&
          [0, 1, 2, 3, 4, 5, 6, 7].map((i) => <SkeletonBar key={i} />)}
        {comparing ? (
          <RunCompare a={compareA} b={compareB} />
        ) : (
          runDetail &&
          (compactRows ? (
            <CompactLumenRun lumenRun={runDetail} detailed />
          ) : (
            <LumenV2 lumenRun={runDetail} detailed />
          ))
        )}
      </SectionBody>
    </SectionCard>
  );
};
